/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { Select } from '../../../../../components/Select'
import { useServiceContext } from '../../../../../root'

interface SelectStepLabelProps {
  name: string
  value: any // Can be option object {value, label} or null
  onChange: (option: any) => void
  placeholder?: string
  isDisabled?: boolean
}

const SelectStepLabel: React.FC<SelectStepLabelProps> = ({
  name,
  value,
  onChange,
  placeholder,
  isDisabled = false
}) => {
  const context = useServiceContext()
  const { translate } = context.service.filter

  const [crStepLabels, setCrStepLabels] = useState<Array<{ value: string; label: string }>>([])
  const [loading, setLoading] = useState(false)

  // Load step labels from API
  useEffect(() => {
    loadStepLabels()
  }, [])

  // Find the matching option from crStepLabels
  const getSelectValue = () => {
    if (!value || !value.value || crStepLabels.length === 0) {
      return null
    }

    // Value is {value, label} format - extract just the value (ID) for Select component
    return value.value
  }

  const loadStepLabels = async () => {
    setLoading(true)
    try {
      const response = (await context.api.settings.getCrTeamTaskStepLabels('')) as any
      // Response format: { elements: [{id: '...', text: '...'}, ...] }
      const labels = response?.elements || []
      const labelOptions = labels.map((label: any) => ({
        value: label.id,
        label: label.text
      }))
      setCrStepLabels(labelOptions)
    } catch (error) {
      setCrStepLabels([])
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (option: any) => {
    // Option is {value, label} format - pass it directly
    onChange(option)
  }

  const selectValue = getSelectValue()

  return (
    <Select
      placeholder={placeholder || translate('SELECT_CR_TASK_LABEL_PLACEHOLDER')}
      name={name}
      value={selectValue}
      options={crStepLabels}
      onChange={handleChange}
      isLoading={loading}
      isDisabled={isDisabled}
      isClearable={true}
      isSearchable={true}
    />
  )
}

export default SelectStepLabel
